import React, { useEffect, useMemo, useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import { usePeriod } from "@/contexts/PeriodContext";
import { api, API_BASE, getToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
    Table,
    TableHeader,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { eur, num } from "@/lib/format";
import { toast } from "sonner";
import {
    Plus,
    Search,
    Download,
    AlertCircle,
    BookOpen,
    Pencil,
    Save,
    ChevronRight,
    Archive,
    PackageSearch,
    ArrowUp,
    ArrowDown,
    ArrowUpDown,
} from "lucide-react";

const ZEITRAUM_OPTIONS = ["2024", "2025", "2026"];
const EMPTY_MOV = {
    isbn: "",
    haupttitel: "",
    zeitraum: String(new Date().getFullYear()),
    zeitraum_typ: "jahr",
    preis_de: 0,
    lager_oe: 0,
    lager_de: 0,
    et: "",
    auflage_nr: 1,
    auflage_anlieferung: "",
    hk_kal: "",
    hk_ist: "",
    lieferstatus: "lieferbar",
    programmhalbjahr: "",
    bemerkungen: "",
};

export default function Inventory() {
    const { activeTenant } = useTenant();
    const { user } = useAuth();
    const { zeitraum, setZeitraum } = usePeriod();
    const isGuest = user?.role === "guest";

    const [titles, setTitles] = useState([]);
    const [titlesLoading, setTitlesLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [showInactive, setShowInactive] = useState(false);
    const [nurNE, setNurNE] = useState(false);
    const [facets, setFacets] = useState({ erloesgruppen: [], programmplaetze: [] });
    const [filterErloes, setFilterErloes] = useState("");
    const [filterProgramm, setFilterProgramm] = useState("");

    const [selectedIsbn, setSelectedIsbn] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Sortierung der Titel-Liste: 'isbn' | 'haupttitel', dir: 'asc' | 'desc'
    const [sortField, setSortField] = useState("isbn");
    const [sortDir, setSortDir] = useState("asc");

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortDir("asc");
        }
    };

    // dialogs
    const [newOpen, setNewOpen] = useState(false);
    const [newForm, setNewForm] = useState(EMPTY_MOV);
    const [editStammOpen, setEditStammOpen] = useState(false);
    const [stammForm, setStammForm] = useState(null);
    const [addMovOpen, setAddMovOpen] = useState(false);
    const [movForm, setMovForm] = useState(EMPTY_MOV);

    // ------------------------- Loaders -------------------------
    const loadTitles = async () => {
        if (!activeTenant) return;
        setTitlesLoading(true);
        try {
            // Immer alle Titel laden — Suche/Filter passieren komplett im Client.
            // So bleibt die Liste konsistent, auch nach Filterwechsel.
            const r = await api.get("/titles", {
                params: {
                    tenant_id: activeTenant.tenant_id,
                    zeitraum,
                    erloesgruppe: filterErloes || undefined,
                    programmplatz: filterProgramm || undefined,
                    status: showInactive ? undefined : "aktiv",
                    nur_ne: nurNE ? true : undefined,
                },
            });
            setTitles(r.data);
        } catch {
            toast.error("Titel konnten nicht geladen werden");
        } finally {
            setTitlesLoading(false);
        }
    };

    const loadFacets = async () => {
        if (!activeTenant) return;
        try {
            const r = await api.get("/titles/facets", { params: { tenant_id: activeTenant.tenant_id } });
            setFacets(r.data);
        } catch {
            /* ignore */
        }
    };

    const loadDetail = async (isbn) => {
        if (!isbn || !activeTenant) return;
        setDetailLoading(true);
        try {
            const r = await api.get(`/titles/${encodeURIComponent(isbn)}`, {
                params: { tenant_id: activeTenant.tenant_id },
            });
            setDetail(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Detail konnte nicht geladen werden");
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };

    useEffect(() => {
        if (!activeTenant) return;
        setSelectedIsbn(null);
        setDetail(null);
        loadTitles();
        loadFacets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTenant?.tenant_id]);

    // Reload titles whenever the selected zeitraum or filters change
    useEffect(() => {
        if (activeTenant) loadTitles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zeitraum, filterErloes, filterProgramm, showInactive, nurNE]);

    useEffect(() => {
        if (selectedIsbn) loadDetail(selectedIsbn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIsbn]);

    // ------------------------- AA-Jahr Inline-Edit -------------------------
    const [aaEditing, setAaEditing] = useState(null); // movement_id
    const [aaEditValue, setAaEditValue] = useState("");
    const [aaSaving, setAaSaving] = useState(false);

    const startAaEdit = (m) => {
        if (isGuest) return;
        setAaEditing(m.id);
        setAaEditValue(m.aa_jahr ? String(m.aa_jahr) : "");
    };
    const cancelAaEdit = () => {
        setAaEditing(null);
        setAaEditValue("");
    };
    const saveAaEdit = async (m) => {
        const raw = aaEditValue.trim();
        let payload;
        if (raw === "") {
            payload = { aa_jahr: null };
        } else {
            const y = parseInt(raw, 10);
            if (Number.isNaN(y)) {
                toast.error("Bitte eine 4-stellige Jahreszahl eingeben");
                return;
            }
            payload = { aa_jahr: y };
        }
        setAaSaving(true);
        try {
            await api.patch(`/movements/${m.id}/aa-jahr`, payload);
            toast.success(`AA-Jahr aktualisiert: ${payload.aa_jahr || "—"}`);
            setAaEditing(null);
            setAaEditValue("");
            await loadDetail(selectedIsbn);
            await loadTitles();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setAaSaving(false);
        }
    };

    // ------------------------- Filters -------------------------
    const filteredTitles = useMemo(() => {
        let list = titles;
        if (!showInactive) list = list.filter((t) => t.aktiv);
        // Client-Suche zusätzlich zur Server-Suche (sofortige Reaktion)
        const q = search.trim().toLowerCase();
        if (q) {
            const qNorm = q.replace(/[^0-9x]/g, "");
            list = list.filter((t) => {
                const isbnRaw = (t.isbn || "").toLowerCase();
                const isbnNorm = (t.isbn_normalized || "").toLowerCase();
                const titel = (t.haupttitel || "").toLowerCase();
                return isbnRaw.includes(q) || (qNorm && isbnNorm.includes(qNorm)) || titel.includes(q);
            });
        }
        // Sortierung
        const dir = sortDir === "asc" ? 1 : -1;
        const sorted = [...list].sort((a, b) => {
            if (sortField === "isbn") {
                const ai = (a.isbn_normalized || (a.isbn || "").replace(/[^0-9X]/gi, "")).toUpperCase();
                const bi = (b.isbn_normalized || (b.isbn || "").replace(/[^0-9X]/gi, "")).toUpperCase();
                return ai.localeCompare(bi) * dir;
            }
            // haupttitel
            const at = (a.haupttitel || "").toLowerCase();
            const bt = (b.haupttitel || "").toLowerCase();
            return at.localeCompare(bt, "de") * dir;
        });
        return sorted;
    }, [titles, showInactive, sortField, sortDir, search]);

    // Auto-Sync der Auswahl mit der gefilterten Liste:
    // - Aktuell ausgewählter Titel nicht mehr in der gefilterten Liste sichtbar
    //   → Auswahl leeren (Detail-Karte wird leer)
    // KEIN Auto-Select beim Tippen — der User wählt selbst (Klick oder Enter)
    useEffect(() => {
        if (selectedIsbn && filteredTitles.length > 0
            && !filteredTitles.some((t) => t.isbn === selectedIsbn)) {
            setSelectedIsbn(null);
            setDetail(null);
        }
    }, [filteredTitles, selectedIsbn]);

    // ------------------------- Actions -------------------------
    const exportCsv = async () => {
        if (!activeTenant) return;
        try {
            const token = getToken();
            const url = `${API_BASE}/export/csv?tenant_id=${activeTenant.tenant_id}&zeitraum=${zeitraum}`;
            const resp = await fetch(url, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const blob = await resp.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = `inventur_${activeTenant.tenant_id}_${zeitraum}.csv`;
            a.click();
            URL.revokeObjectURL(blobUrl);
            toast.success("CSV-Datei heruntergeladen");
        } catch {
            toast.error("Export fehlgeschlagen");
        }
    };

    const openNew = () => {
        setNewForm({ ...EMPTY_MOV, zeitraum });
        setNewOpen(true);
    };

    const submitNew = async () => {
        try {
            const payload = {
                tenant_id: activeTenant.tenant_id,
                ...newForm,
                preis_de: parseFloat(newForm.preis_de) || 0,
                lager_oe: parseInt(newForm.lager_oe) || 0,
                lager_de: parseInt(newForm.lager_de) || 0,
                auflage_nr: parseInt(newForm.auflage_nr) || 0,
                hk_kal: newForm.hk_kal === "" ? null : parseFloat(newForm.hk_kal),
                hk_ist: newForm.hk_ist === "" ? null : parseFloat(newForm.hk_ist),
            };
            await api.post("/movements", payload);
            toast.success("Eintrag angelegt");
            setNewOpen(false);
            await loadTitles();
            setSelectedIsbn(payload.isbn);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        }
    };

    const openEditStamm = () => {
        if (!detail) return;
        setStammForm({ ...detail.stammdaten });
        setEditStammOpen(true);
    };

    const submitStamm = async () => {
        try {
            await api.put(`/titles/${encodeURIComponent(stammForm.isbn)}`, {
                haupttitel: stammForm.haupttitel,
                programmplatz: stammForm.programmplatz,
                erloesgruppe: stammForm.erloesgruppe,
                aktiv: stammForm.aktiv,
            }, { params: { tenant_id: activeTenant.tenant_id } });
            toast.success("Stammdaten gespeichert");
            setEditStammOpen(false);
            await loadTitles();
            await loadDetail(stammForm.isbn);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        }
    };

    const openAddMov = () => {
        if (!detail) return;
        setMovForm({
            ...EMPTY_MOV,
            isbn: detail.stammdaten.isbn,
            haupttitel: detail.stammdaten.haupttitel,
            zeitraum,
        });
        setAddMovOpen(true);
    };

    const submitMov = async () => {
        try {
            const payload = {
                tenant_id: activeTenant.tenant_id,
                ...movForm,
                preis_de: parseFloat(movForm.preis_de) || 0,
                lager_oe: parseInt(movForm.lager_oe) || 0,
                lager_de: parseInt(movForm.lager_de) || 0,
                auflage_nr: parseInt(movForm.auflage_nr) || 0,
                hk_kal: movForm.hk_kal === "" ? null : parseFloat(movForm.hk_kal),
                hk_ist: movForm.hk_ist === "" ? null : parseFloat(movForm.hk_ist),
            };
            await api.post("/movements", payload);
            toast.success("Bewegung hinzugefügt");
            setAddMovOpen(false);
            await loadDetail(movForm.isbn);
            await loadTitles();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        }
    };

    /** Toggle "HK bewusst leer bestätigt" auf einer einzelnen Bewegung (Detail-View). */
    const toggleHkConfirmedNull = async (mov, checked) => {
        try {
            await api.patch("/movements/hk-confirm-null", {
                tenant_id: activeTenant.tenant_id,
                movement_ids: [mov.id],
                confirmed: checked,
            });
            toast.success(
                checked
                    ? 'HK bewusst leer bestätigt'
                    : 'Bestätigung HK bewusst leer aufgehoben',
            );
            await loadDetail(mov.isbn);
            await loadTitles();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        }
    };

    const stamm = detail?.stammdaten;
    const movs = detail?.bewegungen || [];

    return (
        <div className="space-y-6 pb-12 animate-fade-up">
            {/* Header + Toolbar */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Inventar</div>
                    <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-bold mt-1">
                        Stamm- &amp; Bewegungsdaten
                    </h1>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <select
                        value={zeitraum}
                        onChange={(e) => setZeitraum(e.target.value)}
                        className="h-10 px-3 rounded-md border border-border bg-surface1 text-sm"
                        data-testid="inv-zeitraum-select"
                    >
                        {ZEITRAUM_OPTIONS.map((y) => (
                            <option key={y} value={y}>
                                Zeitraum {y}
                            </option>
                        ))}
                    </select>
                    <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
                        <Download className="h-4 w-4 mr-2" /> CSV-Export
                    </Button>
                    {!isGuest && (
                        <Button
                            onClick={openNew}
                            className="h-10 bg-red-500 hover:bg-red-600 text-white shadow-md"
                            data-testid="new-entry-btn"
                        >
                            <Plus className="h-4 w-4 mr-2" /> Neuer Eintrag
                        </Button>
                    )}
                </div>
            </div>

            {/* Stack-Layout: 3 Kacheln untereinander */}
            <div className="space-y-5">
                {/* TOP: Kompakte Titel-Auswahl */}
                <Card className="p-4 bg-surface1 border-border" data-testid="title-list-card">
                    <div className="flex flex-wrap items-end gap-3 mb-3">
                        <div className="flex-1 min-w-[260px]">
                            <Label className="text-xs">Suche</Label>
                            <div className="relative mt-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="ISBN oder Titel suchen…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && filteredTitles.length > 0) {
                                            setSelectedIsbn(filteredTitles[0].isbn);
                                        }
                                    }}
                                    data-testid="title-search"
                                    className="h-9 pl-9"
                                />
                            </div>
                        </div>
                        <div className="min-w-[200px]">
                            <Label className="text-xs">Verlagsbereich</Label>
                            <select
                                value={filterErloes}
                                onChange={(e) => setFilterErloes(e.target.value)}
                                className="mt-1 h-9 w-full px-2 rounded-md border border-border bg-surface2 text-xs"
                                data-testid="filter-erloesgruppe"
                            >
                                <option value="">Alle</option>
                                {facets.erloesgruppen.map((e) => (
                                    <option key={e} value={e}>{e}</option>
                                ))}
                            </select>
                        </div>
                        <div className="min-w-[200px]">
                            <Label className="text-xs">Programmplatz</Label>
                            <select
                                value={filterProgramm}
                                onChange={(e) => setFilterProgramm(e.target.value)}
                                className="mt-1 h-9 w-full px-2 rounded-md border border-border bg-surface2 text-xs"
                                data-testid="filter-programmplatz"
                            >
                                <option value="">Alle</option>
                                {facets.programmplaetze.map((p) => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-2 pb-2">
                            <Switch
                                checked={showInactive}
                                onCheckedChange={setShowInactive}
                                data-testid="show-inactive-switch"
                                id="inv-show-inactive"
                            />
                            <Label htmlFor="inv-show-inactive" className="text-xs cursor-pointer">Inaktive zeigen</Label>
                        </div>
                        <div className="flex items-center gap-2 pb-2">
                            <Switch
                                checked={nurNE}
                                onCheckedChange={setNurNE}
                                data-testid="filter-ne-switch"
                                id="inv-nur-ne"
                            />
                            <Label
                                htmlFor="inv-nur-ne"
                                className="text-xs cursor-pointer flex items-center gap-1.5"
                                title={`Neuerscheinungen für Periode ${zeitraum}: ET-Jahr ∈ {${zeitraum}, ${parseInt(zeitraum) + 1}}`}
                            >
                                <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/40">
                                    NE
                                </span>
                                Nur Neuerscheinungen
                            </Label>
                        </div>
                    </div>

                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-2 px-1">
                        <span>
                            {filteredTitles.length} {filteredTitles.length === 1 ? "Treffer" : "Titel"}
                            {search.trim() && filteredTitles.length > 0 && (
                                <span className="ml-2 text-tenant">· Enter wählt den ersten Treffer</span>
                            )}
                        </span>
                        {selectedIsbn && (
                            <span className="font-mono">Aktiv: {selectedIsbn}</span>
                        )}
                    </div>

                    {/* Spalten-Header mit Sortierung */}
                    <div className="flex items-center gap-3 px-3 py-2 border border-b-0 border-border rounded-t-md bg-surface2 text-[10px] uppercase tracking-widest text-muted-foreground">
                        <button
                            type="button"
                            onClick={() => toggleSort("isbn")}
                            className="flex items-center gap-1 w-[130px] shrink-0 hover:text-foreground transition-colors"
                            data-testid="sort-isbn-btn"
                        >
                            ISBN
                            {sortField === "isbn" ? (
                                sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : (
                                <ArrowUpDown className="h-3 w-3 opacity-40" />
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => toggleSort("haupttitel")}
                            className="flex items-center gap-1 flex-1 text-left hover:text-foreground transition-colors"
                            data-testid="sort-haupttitel-btn"
                        >
                            Haupttitel
                            {sortField === "haupttitel" ? (
                                sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                            ) : (
                                <ArrowUpDown className="h-3 w-3 opacity-40" />
                            )}
                        </button>
                        <span className="w-[80px] text-right shrink-0">Bewegungen</span>
                        <span className="w-[130px] text-right shrink-0">Bilanz</span>
                        <span className="w-[14px] shrink-0" />
                    </div>

                    {/* Kompakte Liste: ca. 3-4 Zeilen sichtbar, scrollbar */}
                    <div className="border border-border rounded-b-md max-h-[160px] overflow-y-auto" data-testid="title-list-scroll">
                        {titlesLoading && (
                            <div className="flex items-center justify-center gap-3 py-10 px-4 bg-tenant-soft/40 animate-pulse" data-testid="title-list-loading">
                                <span className="inline-block w-5 h-5 border-2 border-tenant border-t-transparent rounded-full animate-spin" />
                                <div>
                                    <div className="text-sm font-medium text-foreground">Daten werden geladen…</div>
                                    <div className="text-[11px] text-muted-foreground">Einen Moment bitte, Inventur wird vorbereitet.</div>
                                </div>
                            </div>
                        )}
                        {!titlesLoading && filteredTitles.length === 0 && (
                            <div className="text-center py-6 text-sm text-muted-foreground px-4">
                                Keine Titel gefunden.
                            </div>
                        )}
                        {filteredTitles.map((t) => {
                            const active = selectedIsbn === t.isbn;
                            return (
                                <button
                                    key={t.isbn}
                                    onClick={() => setSelectedIsbn(t.isbn)}
                                    data-testid={`title-row-${t.isbn}`}
                                    className={`w-full text-left px-3 py-2 border-b border-border last:border-b-0 transition-colors flex items-center gap-3 ${
                                        active ? "bg-tenant-soft border-l-2 border-l-tenant" : "hover:bg-surface2"
                                    }`}
                                >
                                    <div className="font-mono text-[11px] text-muted-foreground w-[130px] shrink-0">{t.isbn}</div>
                                    <div className="text-sm font-medium truncate flex-1" title={t.haupttitel}>{t.haupttitel}</div>
                                    <div className="text-[11px] text-muted-foreground shrink-0 w-[80px] text-right">
                                        {t.anzahl_bewegungen || 0} Bew.
                                    </div>
                                    {!t.aktiv && (
                                        <Badge variant="outline" className="text-[9px] py-0 px-1.5 uppercase tracking-widest shrink-0">
                                            Inaktiv
                                        </Badge>
                                    )}
                                    {t.tag_ne && (
                                        <span
                                            className="text-[9px] py-0 px-1.5 rounded font-bold uppercase tracking-widest shrink-0 bg-amber-500/20 text-amber-300 border border-amber-500/40"
                                            title={`Neuerscheinung im Jahr ${zeitraum} (ET ∈ {${zeitraum}, ${parseInt(zeitraum) + 1}})`}
                                            data-testid={`ne-badge-${t.isbn}`}
                                        >
                                            NE
                                        </span>
                                    )}
                                    {t.tag_nd && (
                                        <span
                                            className="text-[9px] py-0 px-1.5 rounded font-bold uppercase tracking-widest shrink-0 bg-teal-500/20 text-teal-300 border border-teal-500/40"
                                            title={`Nachdruck im Jahr ${zeitraum} (AA-Jahr = ${zeitraum})`}
                                            data-testid={`nd-badge-${t.isbn}`}
                                        >
                                            ND
                                        </span>
                                    )}
                                    {t.tag_r && (
                                        <span
                                            className="text-[9px] py-0 px-1.5 rounded font-bold uppercase tracking-widest shrink-0 bg-rose-500/20 text-rose-300 border border-rose-500/40"
                                            title={`Ramsch im Jahr ${zeitraum} (Sonder-WB Typ Verramschung)`}
                                            data-testid={`r-badge-${t.isbn}`}
                                        >
                                            R
                                        </span>
                                    )}
                                    {t.tag_m && (
                                        <span
                                            className="text-[9px] py-0 px-1.5 rounded font-bold uppercase tracking-widest shrink-0 bg-purple-500/20 text-purple-300 border border-purple-500/40"
                                            title={`Makulatur im Jahr ${zeitraum} (Sonder-WB Typ Makulatur)`}
                                            data-testid={`m-badge-${t.isbn}`}
                                        >
                                            M
                                        </span>
                                    )}
                                    {t.snapshot_kein_datensatz ? (
                                        <span className="text-[10px] italic text-muted-foreground shrink-0 w-[130px] text-right">
                                            kein Datensatz {zeitraum}
                                        </span>
                                    ) : t.snapshot_zeitraum ? (
                                        <span className="text-right shrink-0 w-[130px]">
                                            <span className="block text-[9px] uppercase tracking-widest opacity-70">
                                                Bilanz {t.snapshot_zeitraum}
                                            </span>
                                            <span className="block tabular text-foreground font-medium text-xs">
                                                {eur(t.snapshot_bilanzwert)}
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="shrink-0 w-[130px]" />
                                    )}
                                    <ChevronRight className={`h-3.5 w-3.5 shrink-0 ${active ? "text-tenant" : "text-muted-foreground"}`} />
                                </button>
                            );
                        })}
                    </div>
                </Card>

                {/* MITTE + UNTEN: Detail */}
                <div className="space-y-5 min-w-0">
                    {!stamm && !detailLoading && (
                        <Card className="p-12 bg-surface1 border-border text-center">
                            <PackageSearch className="h-10 w-10 mx-auto text-muted-foreground" />
                            <h3 className="font-heading text-lg mt-3">Bitte einen Titel auswählen</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                                Wählen Sie oben einen Titel, um Stammdaten und Bewegungsdaten zu sehen.
                            </p>
                        </Card>
                    )}

                    {detailLoading && (
                        <Card className="p-12 bg-surface1 border-border text-center text-sm text-muted-foreground">
                            Lade Titel-Details…
                        </Card>
                    )}

                    {stamm && (
                        <>
                            {/* STAMMDATEN */}
                            <Card className="p-6 bg-surface1 border-border" data-testid="stammdaten-card">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                            <BookOpen className="h-3.5 w-3.5" /> Stammdaten
                                            {!stamm.aktiv && (
                                                <Badge variant="outline" className="text-[10px] uppercase ml-1">
                                                    Archiviert
                                                </Badge>
                                            )}
                                        </div>
                                        <h2 className="font-heading text-2xl tracking-tight font-semibold mt-1">
                                            {stamm.haupttitel}
                                        </h2>
                                        <div className="font-mono text-xs text-muted-foreground mt-0.5">{stamm.isbn}</div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <select
                                            value={zeitraum}
                                            onChange={(e) => setZeitraum(e.target.value)}
                                            className="h-9 px-3 rounded-md border border-border bg-surface2 text-sm"
                                            data-testid="stamm-zeitraum-select"
                                            title="Bewertungszeitraum wählen"
                                        >
                                            {ZEITRAUM_OPTIONS.map((y) => (
                                                <option key={y} value={y}>
                                                    Zeitraum {y}
                                                </option>
                                            ))}
                                        </select>
                                        {!isGuest && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={openEditStamm}
                                                data-testid="edit-stamm-btn"
                                            >
                                                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Bearbeiten
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                            Programmplatz
                                        </div>
                                        <div className="text-sm mt-1">{stamm.programmplatz || "—"}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                            Erlösgruppe
                                        </div>
                                        <div className="text-sm mt-1">{stamm.erloesgruppe || "—"}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                            Status
                                        </div>
                                        <div className="text-sm mt-1">
                                            {stamm.aktiv ? (
                                                <span className="inline-flex items-center gap-1 text-emerald-400">
                                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Aktiv
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-muted-foreground">
                                                    <Archive className="h-3 w-3" /> Inaktiv
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                            Bewegungen gesamt
                                        </div>
                                        <div className="text-sm mt-1 tabular">{movs.length}</div>
                                    </div>
                                </div>
                                {/* Snapshot für den gewählten Bewertungszeitraum */}
                                {(() => {
                                    const sel = movs.find((m) => m.zeitraum === zeitraum);
                                    return (
                                        <div className="mt-5 pt-5 border-t border-border">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                                    Bewertung Zeitraum {zeitraum}
                                                </div>
                                                {sel && (
                                                    <Badge variant="secondary" className="text-[10px]">
                                                        {sel.alter_jahre}. Jahr · WB {sel.wb_prozent}%
                                                        {sel.massgeblich_quelle === "aa" && (
                                                            <span className="ml-1 text-emerald-400">· AA {sel.aa_jahr}</span>
                                                        )}
                                                    </Badge>
                                                )}
                                            </div>
                                            {!sel ? (
                                                <div className="text-sm text-muted-foreground italic">
                                                    Kein Datensatz für Zeitraum {zeitraum} vorhanden. Bitte ein anderes Jahr wählen oder Bewegung hinzufügen.
                                                </div>
                                            ) : (
                                                <div className="grid sm:grid-cols-4 gap-4">
                                                    <div>
                                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                                            Lagerbestand
                                                        </div>
                                                        <div className="font-heading text-xl tabular mt-1">
                                                            {num(sel.lager_gesamt)}{" "}
                                                            <span className="text-xs text-muted-foreground font-sans">Stk.</span>
                                                        </div>
                                                        <div className="text-[10px] text-muted-foreground mt-0.5">
                                                            Ö {num(sel.lager_oe)} · D {num(sel.lager_de)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                                            Lagerwert
                                                        </div>
                                                        <div className="font-heading text-xl tabular mt-1">
                                                            {eur(sel.lagerwert)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                                            Wertberichtigung
                                                        </div>
                                                        <div className="font-heading text-xl tabular mt-1 text-yellow-400">
                                                            {eur(sel.wertberichtigung)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                                            Bilanzwert
                                                        </div>
                                                        <div className="font-heading text-xl tabular mt-1 text-emerald-400">
                                                            {eur(sel.bilanzwert)}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </Card>

                            {/* BEWEGUNGSDATEN */}
                            <Card className="p-6 bg-surface1 border-border" data-testid="bewegungen-card">
                                <div className="flex items-center justify-between gap-3 mb-4">
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                            Bewegungsdaten
                                        </div>
                                        <h3 className="font-heading text-xl font-semibold mt-1">
                                            Pro Zeitraum
                                        </h3>
                                    </div>
                                    {!isGuest && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={openAddMov}
                                            data-testid="add-mov-btn"
                                        >
                                            <Plus className="h-3.5 w-3.5 mr-1.5" /> Bewegung hinzufügen
                                        </Button>
                                    )}
                                </div>
                                <div className="overflow-auto border border-border rounded-md">
                                    <Table>
                                        <TableHeader className="bg-surface2 sticky top-0">
                                            <TableRow>
                                                <TableHead className="text-xs uppercase tracking-widest">Zeitraum</TableHead>
                                                <TableHead className="text-xs uppercase tracking-widest" title="Erscheinungstermin-Jahr">ET</TableHead>
                                                <TableHead className="text-xs uppercase tracking-widest" title="Aktuelle Auflage: Anliefertermin (Jahr)">AA</TableHead>
                                                <TableHead className="text-xs uppercase tracking-widest text-right">Lager Ö</TableHead>
                                                <TableHead className="text-xs uppercase tracking-widest text-right">Lager D</TableHead>
                                                <TableHead className="text-xs uppercase tracking-widest text-right">Gesamt</TableHead>
                                                <TableHead className="text-xs uppercase tracking-widest text-right">HK</TableHead>
                                                <TableHead className="text-xs uppercase tracking-widest text-right">Lagerwert</TableHead>
                                                <TableHead className="text-xs uppercase tracking-widest text-right">Wertberichtigung</TableHead>
                                                <TableHead className="text-xs uppercase tracking-widest text-right">Bilanzwert</TableHead>
                                                <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {movs.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={11} className="text-center text-sm text-muted-foreground py-8">
                                                        Keine Bewegungsdaten vorhanden.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {movs.map((m) => {
                                                const critical = (m.wb_prozent || 0) >= 75;
                                                const isCurrent = m.zeitraum === zeitraum;
                                                const usesAa = m.massgeblich_quelle === "aa";
                                                const isSonder = m.massgeblich_quelle === "sonder_wb" || m.sonder_wb;
                                                return (
                                                    <TableRow key={m.id} data-testid={`mov-row-${m.id}`} className={`${isCurrent ? "bg-tenant-soft/40" : ""} ${isSonder ? "border-l-2 border-l-red-500" : ""}`}>
                                                        <TableCell>
                                                            <div className="font-medium">{m.zeitraum}</div>
                                                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                                                {m.zeitraum_typ}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell
                                                            className={`text-xs tabular ${(usesAa || isSonder) ? "text-muted-foreground line-through" : "font-medium"}`}
                                                            title={isSonder ? "Sonder-WB überschreibt ET/AA" : (usesAa ? "ET wird nicht verwendet — AA ist maßgeblich (Neuauflage oder Vorzieh-Anlieferung)" : "ET ist maßgeblich für die Wertberichtigung")}
                                                        >
                                                            {m.et_jahr || "—"}
                                                        </TableCell>
                                                        <TableCell
                                                            className={`text-xs tabular ${usesAa && !isSonder ? "font-medium text-emerald-400" : "text-muted-foreground"} ${isSonder ? "line-through" : ""}`}
                                                            title={isSonder ? "Sonder-WB überschreibt ET/AA" : (usesAa ? "AA ist maßgeblich für die Wertberichtigung (Neuauflage)" : "AA ist nicht maßgeblich. Klick zum Bearbeiten.")}
                                                        >
                                                            {aaEditing === m.id ? (
                                                                <div className="flex items-center gap-1">
                                                                    <Input
                                                                        autoFocus
                                                                        value={aaEditValue}
                                                                        onChange={(e) => setAaEditValue(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === "Enter") saveAaEdit(m);
                                                                            if (e.key === "Escape") cancelAaEdit();
                                                                        }}
                                                                        onBlur={() => saveAaEdit(m)}
                                                                        placeholder="—"
                                                                        className="h-6 w-[60px] px-1 text-xs tabular"
                                                                        disabled={aaSaving}
                                                                        data-testid={`aa-edit-input-${m.id}`}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => startAaEdit(m)}
                                                                    disabled={isGuest}
                                                                    className={`px-1.5 py-0.5 rounded ${!isGuest ? "hover:bg-tenant-soft cursor-pointer" : "cursor-default"} text-left`}
                                                                    data-testid={`aa-cell-${m.id}`}
                                                                >
                                                                    {m.aa_jahr || "—"}
                                                                </button>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-right tabular">{num(m.lager_oe)}</TableCell>
                                                        <TableCell className="text-right tabular">{num(m.lager_de)}</TableCell>
                                                        <TableCell className="text-right tabular font-medium">{num(m.lager_gesamt)}</TableCell>
                                                        <TableCell className="text-right tabular text-xs">
                                                            {m.hk_ist != null ? (
                                                                <>{eur(m.hk_ist)}<div className="text-[9px] text-muted-foreground uppercase">ist</div></>
                                                            ) : m.hk_kal != null ? (
                                                                <span className="text-muted-foreground">{eur(m.hk_kal)}<div className="text-[9px] uppercase">kal</div></span>
                                                            ) : "—"}
                                                        </TableCell>
                                                        <TableCell className="text-right tabular">{eur(m.lagerwert)}</TableCell>
                                                        <TableCell className="text-right tabular">
                                                            <div className="font-medium text-yellow-400">{eur(m.wertberichtigung)}</div>
                                                            <span
                                                                className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] ${
                                                                    isSonder
                                                                        ? "bg-red-500/20 text-red-300"
                                                                        : critical
                                                                        ? "bg-red-500/15 text-red-400"
                                                                        : m.wb_prozent > 0
                                                                        ? "bg-yellow-500/15 text-yellow-400"
                                                                        : "bg-emerald-500/15 text-emerald-400"
                                                                }`}
                                                            >
                                                                {isSonder ? "Sonder-WB · 100%" : `${m.wb_prozent}% · ${m.alter_jahre}. Jahr`}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell className="text-right tabular font-semibold text-emerald-400">{eur(m.bilanzwert)}</TableCell>
                                                        <TableCell>
                                                            {(() => {
                                                                const hkMissing =
                                                                    (!m.hk_ist || m.hk_ist === 0) &&
                                                                    (!m.hk_kal || m.hk_kal === 0);
                                                                if (m.hk_confirmed_null) {
                                                                    return (
                                                                        <button
                                                                            type="button"
                                                                            disabled={isGuest}
                                                                            onClick={() => toggleHkConfirmedNull(m, false)}
                                                                            title='Klicken, um HK bewusst leer aufzuheben'
                                                                            className="inline-flex items-center gap-1 rounded border border-muted-foreground/50 px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-muted-foreground/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                            data-testid={`mov-hk-null-off-${m.id}`}
                                                                        >
                                                                            HK ø
                                                                        </button>
                                                                    );
                                                                }
                                                                if (m.fehler) {
                                                                    return (
                                                                        <div className="flex flex-col items-start gap-1">
                                                                            <Badge variant="destructive" className="gap-1 text-[10px]">
                                                                                <AlertCircle className="h-2.5 w-2.5" /> Fehler
                                                                            </Badge>
                                                                            {hkMissing && !isGuest && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => toggleHkConfirmedNull(m, true)}
                                                                                    title='Als HK bewusst leer bestätigen — Titel verschwindet aus dem Fehlerprotokoll'
                                                                                    className="text-[10px] text-muted-foreground underline decoration-dotted hover:text-foreground"
                                                                                    data-testid={`mov-hk-null-on-${m.id}`}
                                                                                >
                                                                                    HK bewusst leer?
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }
                                                                return (
                                                                    <Badge variant="secondary" className="text-[10px]">
                                                                        {m.lieferstatus || "lieferbar"}
                                                                    </Badge>
                                                                );
                                                            })()}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            </Card>
                        </>
                    )}
                </div>
            </div>

            {/* DIALOGS */}
            <MovementDialog
                open={newOpen}
                onOpenChange={setNewOpen}
                form={newForm}
                setForm={setNewForm}
                onSubmit={submitNew}
                title="Neuer Eintrag"
                description="Legt einen neuen Titel + erste Bewegung an."
                allowIsbn
            />

            <MovementDialog
                open={addMovOpen}
                onOpenChange={setAddMovOpen}
                form={movForm}
                setForm={setMovForm}
                onSubmit={submitMov}
                title={`Bewegung hinzufügen · ${stamm?.haupttitel || ""}`}
                description="Fügt eine neue Bewegungs-Zeile zu diesem Titel hinzu."
            />

            <Dialog open={editStammOpen} onOpenChange={setEditStammOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Stammdaten bearbeiten</DialogTitle>
                        <DialogDescription>ISBN: {stammForm?.isbn}</DialogDescription>
                    </DialogHeader>
                    {stammForm && (
                        <div className="space-y-3">
                            <div>
                                <Label>Haupttitel</Label>
                                <Input
                                    value={stammForm.haupttitel || ""}
                                    onChange={(e) => setStammForm({ ...stammForm, haupttitel: e.target.value })}
                                    data-testid="stamm-haupttitel"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Programmplatz</Label>
                                    <Input
                                        value={stammForm.programmplatz || ""}
                                        onChange={(e) => setStammForm({ ...stammForm, programmplatz: e.target.value })}
                                        data-testid="stamm-programmplatz"
                                    />
                                </div>
                                <div>
                                    <Label>Erlösgruppe</Label>
                                    <Input
                                        value={stammForm.erloesgruppe || ""}
                                        onChange={(e) => setStammForm({ ...stammForm, erloesgruppe: e.target.value })}
                                        data-testid="stamm-erloesgruppe"
                                    />
                                </div>
                            </div>
                            <div className="flex items-center justify-between rounded-md border border-border p-3 bg-surface2">
                                <div>
                                    <Label className="cursor-pointer">Aktiv</Label>
                                    <div className="text-xs text-muted-foreground">
                                        Inaktive Titel werden in Berechnungen ignoriert (nicht gelöscht).
                                    </div>
                                </div>
                                <Switch
                                    checked={!!stammForm.aktiv}
                                    onCheckedChange={(v) => setStammForm({ ...stammForm, aktiv: v })}
                                    data-testid="stamm-aktiv-switch"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEditStammOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={submitStamm} className="bg-tenant text-[color:var(--tenant-accent-foreground)]" data-testid="stamm-save">
                            <Save className="h-4 w-4 mr-2" /> Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// -------------------- Movement Dialog --------------------
function MovementDialog({ open, onOpenChange, form, setForm, onSubmit, title, description, allowIsbn = false }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="font-heading">{title}</DialogTitle>
                    {description && <DialogDescription>{description}</DialogDescription>}
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
                    <div>
                        <Label>ISBN</Label>
                        <Input
                            value={form.isbn}
                            onChange={(e) => setForm({ ...form, isbn: e.target.value })}
                            disabled={!allowIsbn}
                            data-testid="mov-isbn"
                        />
                    </div>
                    <div>
                        <Label>Haupttitel</Label>
                        <Input
                            value={form.haupttitel}
                            onChange={(e) => setForm({ ...form, haupttitel: e.target.value })}
                            data-testid="mov-titel"
                        />
                    </div>
                    <div>
                        <Label>Zeitraum</Label>
                        <Input
                            value={form.zeitraum}
                            onChange={(e) => setForm({ ...form, zeitraum: e.target.value })}
                            placeholder="2026 / 2026-Q1 / 2026-03"
                            data-testid="mov-zeitraum"
                        />
                    </div>
                    <div>
                        <Label>Zeitraum-Typ</Label>
                        <select
                            value={form.zeitraum_typ}
                            onChange={(e) => setForm({ ...form, zeitraum_typ: e.target.value })}
                            className="mt-1 h-10 w-full px-3 rounded-md border border-border bg-surface1 text-sm"
                            data-testid="mov-zeitraum-typ"
                        >
                            <option value="jahr">Jahr</option>
                            <option value="quartal">Quartal</option>
                            <option value="monat">Monat</option>
                        </select>
                    </div>
                    <div>
                        <Label>Lager Ö</Label>
                        <Input
                            type="number"
                            value={form.lager_oe}
                            onChange={(e) => setForm({ ...form, lager_oe: e.target.value })}
                            data-testid="mov-lager-oe"
                        />
                    </div>
                    <div>
                        <Label>Lager D</Label>
                        <Input
                            type="number"
                            value={form.lager_de}
                            onChange={(e) => setForm({ ...form, lager_de: e.target.value })}
                            data-testid="mov-lager-de"
                        />
                    </div>
                    <div>
                        <Label>HK Ist (€)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            value={form.hk_ist}
                            onChange={(e) => setForm({ ...form, hk_ist: e.target.value })}
                            data-testid="mov-hk-ist"
                        />
                    </div>
                    <div>
                        <Label>HK Kal (€)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            value={form.hk_kal}
                            onChange={(e) => setForm({ ...form, hk_kal: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label>Preis DE (€)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            value={form.preis_de}
                            onChange={(e) => setForm({ ...form, preis_de: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label>Erscheinungstermin</Label>
                        <Input
                            value={form.et}
                            onChange={(e) => setForm({ ...form, et: e.target.value })}
                            placeholder="2024-03-15"
                        />
                    </div>
                    <div>
                        <Label>Auflage Nr.</Label>
                        <Input
                            type="number"
                            value={form.auflage_nr}
                            onChange={(e) => setForm({ ...form, auflage_nr: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label>Auflage Anlieferung</Label>
                        <Input
                            value={form.auflage_anlieferung}
                            onChange={(e) => setForm({ ...form, auflage_anlieferung: e.target.value })}
                            placeholder="2024-04-01"
                        />
                    </div>
                    <div>
                        <Label>Lieferstatus</Label>
                        <Input
                            value={form.lieferstatus}
                            onChange={(e) => setForm({ ...form, lieferstatus: e.target.value })}
                            placeholder="lieferbar"
                        />
                    </div>
                    <div>
                        <Label>Programmhalbjahr</Label>
                        <Input
                            value={form.programmhalbjahr}
                            onChange={(e) => setForm({ ...form, programmhalbjahr: e.target.value })}
                            placeholder="2025/1"
                        />
                    </div>
                    <div className="col-span-2">
                        <Label>Bemerkungen</Label>
                        <Input
                            value={form.bemerkungen}
                            onChange={(e) => setForm({ ...form, bemerkungen: e.target.value })}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={onSubmit}
                        className="bg-red-500 hover:bg-red-600 text-white"
                        disabled={!form.isbn || !form.haupttitel}
                        data-testid="mov-submit"
                    >
                        <Save className="h-4 w-4 mr-2" /> Speichern
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
